import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import config from '../config';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { formatDisplayDate } from '../utils/formatDate';

const BusinessPrivacyPage = () => {
    const [pageData, setPageData] = useState({
        title: 'Business Privacy Policy',
        content: '',
        version: '1.0',
        effective_date: null
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPrivacyPolicy = async () => {
            try {
                const res = await fetch(`${config.API_BASE_URL}/api/tos/privacy`);
                if (!res.ok) {
                    // Try fallback to public content
                    const pubRes = await fetch(`${config.API_BASE_URL}/api/public/content/business-privacy`);
                    if (pubRes.ok) {
                        const data = await pubRes.json();
                        setPageData({
                            title: data.title || 'Business Privacy Policy',
                            content: data.content || '',
                            version: data.version || '1.0',
                            effective_date: data.effective_date || null
                        });
                        return;
                    }
                    throw new Error('Could not load privacy policy');
                }
                const data = await res.json();
                setPageData({
                    title: data.title || 'Business Privacy Policy',
                    content: data.content || '',
                    version: data.version || '1.0',
                    effective_date: data.effective_date || null
                });
            } catch (err) {
                console.error('Failed to fetch Privacy Policy:', err);
                toast.error('Failed to load Privacy Policy');
            } finally {
                setLoading(false);
            }
        };

        fetchPrivacyPolicy();
    }, []);

    const handleCopyLink = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Policy link copied to clipboard');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <MasterLayout>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <Breadcrumb title="Privacy Policy" />
                
                {/* Legal Document Navigation Tabs */}
                <div className="d-flex align-items-center gap-2 p-1 bg-neutral-100 radius-8">
                    <NavLink
                        to="/tos"
                        className={({ isActive }) => `btn btn-sm px-16 py-6 radius-6 fw-semibold d-inline-flex align-items-center gap-1 transition-2 ${isActive ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                    >
                        <Icon icon="solar:document-text-linear" className="text-base" />
                        <span>Terms of Service</span>
                    </NavLink>
                    <NavLink
                        to="/privacy"
                        className={({ isActive }) => `btn btn-sm px-16 py-6 radius-6 fw-semibold d-inline-flex align-items-center gap-1 transition-2 ${isActive ? 'btn-primary text-white shadow-xs' : 'btn-outline-neutral text-secondary-light border-0'}`}
                    >
                        <Icon icon="solar:shield-check-bold" className="text-base" />
                        <span>Privacy Policy</span>
                    </NavLink>
                </div>
            </div>

            <div className="container-fluid py-2">
                <style>{`
                    .tos-content h1,
                    .tos-content h2,
                    .tos-content h3,
                    .tos-content h4,
                    .tos-content h5,
                    .tos-content .section-title,
                    .tos-content [style*="border"] {
                        border: none !important;
                        border-left: none !important;
                        background: transparent !important;
                        box-shadow: none !important;
                    }
                    .tos-content h1,
                    .tos-content h2,
                    .tos-content h3,
                    .tos-content h4 {
                        font-weight: 700 !important;
                        color: var(--hs-text-primary, #0f172a) !important;
                        padding: 0 0 8px 0 !important;
                        margin-top: 24px !important;
                        margin-bottom: 12px !important;
                        border-bottom: 1px solid var(--hs-border, #e2e8f0) !important;
                    }
                    [data-theme='dark'] .tos-content,
                    [data-theme='dark'] .tos-content p,
                    [data-theme='dark'] .tos-content li,
                    [data-theme='dark'] .tos-content span {
                        color: var(--hs-text-secondary) !important;
                    }
                `}</style>

                <div className="row justify-content-center">
                    <div className="col-12 col-xl-10">
                        <div
                            className="card border radius-16 overflow-hidden d-flex flex-column bg-white shadow-xs"
                            style={{ minHeight: '560px' }}
                        >
                            {/* Header */}
                            <div className="card-header bg-white py-16 px-24 d-flex flex-wrap justify-content-between align-items-center gap-3 border-bottom flex-shrink-0">
                                <div>
                                    <h6 className="mb-1 fw-bold text-dark d-flex align-items-center gap-2">
                                        <Icon icon="solar:shield-check-bold" className="text-primary-600 text-xl" />
                                        <span>{pageData.title}</span>
                                    </h6>
                                    <div className="d-flex align-items-center gap-2 text-xs text-secondary-light">
                                        <span className="badge bg-neutral-200 text-neutral-700 radius-pill px-2 py-0">
                                            v{pageData.version}
                                        </span>
                                        {pageData.effective_date && (
                                            <span>Effective {formatDisplayDate(pageData.effective_date)}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCopyLink}
                                        className="btn btn-sm btn-outline-neutral radius-8 px-12 py-6 d-inline-flex align-items-center gap-1 text-xs"
                                        title="Copy Link"
                                    >
                                        <Icon icon="solar:link-linear" className="text-sm" />
                                        <span>Share</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrint}
                                        className="btn btn-sm btn-outline-neutral radius-8 px-12 py-6 d-inline-flex align-items-center gap-1 text-xs"
                                        title="Print Document"
                                    >
                                        <Icon icon="solar:printer-linear" className="text-sm" />
                                        <span>Print</span>
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="card-body p-24 p-md-32 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-secondary-light mt-2 text-sm">Loading Business Privacy Policy...</p>
                                    </div>
                                ) : (
                                    <div
                                        className="tos-content text-dark"
                                        style={{ fontSize: '14.5px', lineHeight: '1.8' }}
                                        dangerouslySetInnerHTML={{ __html: pageData.content || '<p class="text-secondary-light">Privacy Policy is currently being updated.</p>' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MasterLayout>
    );
};

export default BusinessPrivacyPage;
